import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { itemsGetAll } from "~/api-refs";
import { Button } from "~/components/ui/button";
import { Combobox } from "~/components/ui/combobox";

import type { ItemFormData, ModalTypes } from "./types";

export const DuplicateItemForm = ({
  setItem,
  setModalType,
}: {
  setItem: (item: ItemFormData) => void;
  setModalType: (type: ModalTypes | null) => void;
}) => {
  const { data: itemsFromMerchantResult } = useQuery(
    convexQuery(itemsGetAll, {
      includeCategoryRelations: true,
      includeMenus: true,
      includeModifierGroupsCount: true,
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const itemsFromMerchant = itemsFromMerchantResult?.page ?? [];
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const handleItemSelect = (itemId: string) => {
    const selection = itemsFromMerchant.find((item) => item._id === itemId);
    if (selection) {
      setItem({
        name: selection.name,
        shortName: selection.shortName,
        imageUrl: selection.imageUrl ?? null,
        description: selection.description,
        price:
          typeof selection.price === "object"
            ? Math.max(
                ...Object.values(selection.price).map((d) =>
                  Number.parseFloat(typeof d === "string" ? d : String(d))
                ),
                0
              ).toString()
            : selection.price.toString(),
        isOutOfStock: selection.isOutOfStock,
        categories: selection.categoryRelations.map((cr) => cr.category._id),
        taxCategoryId: selection.taxCategoryId ?? null,
        salesCategoryId: selection.salesCategoryId ?? null,
      });
    }
  };

  return (
    <>
      <label className="body-sm" htmlFor="item">
        Choose an item to duplicate:
      </label>
      <Combobox
        className="w-full"
        dropdownClassName="w-130"
        items={itemsFromMerchant.map((item) => ({
          label: item.name,
          value: item._id,
          keywords: [item.name],
          component: (
            <div className="w-full space-y-1">
              <div className="flex w-full items-center justify-between gap-1">
                <p className="text-foreground">{item.name}</p>
                {item.modifierGroupsCount !== "0" && (
                  <p className="caption bg-muted text-foreground rounded-full px-2 py-1">
                    {item.modifierGroupsCount} mod. group
                    {item.modifierGroupsCount !== "1" && "s"}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1">
                {item.categoryRelations.map((categoryRelation) => (
                  <div
                    className="caption bg-category-bg text-category rounded-full px-2 py-1"
                    key={categoryRelation.category.id}
                  >
                    {categoryRelation.category.name}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1">
                {item.categoryRelations
                  .flatMap((cr) => cr.category.menuRelations)
                  .filter(
                    (
                      menuRelation
                    ): menuRelation is NonNullable<typeof menuRelation> =>
                      Boolean(menuRelation)
                  )
                  .map((menuRelation) => (
                    <div
                      className="caption bg-menu-bg text-menu rounded-full px-2 py-1"
                      key={menuRelation.menu.id}
                    >
                      {menuRelation.menu.name}
                    </div>
                  ))}
              </div>
            </div>
          ),
        }))}
        onValueChange={setSelectedItem}
        placeholder="Select Item..."
        value={selectedItem}
      />
      <div className="mt-2 flex space-x-2">
        <Button
          className="flex-1"
          onClick={() => setModalType(null)}
          type="button"
          variant="secondary"
        >
          Back
        </Button>
        <Button
          className="flex-1"
          onClick={() => selectedItem && handleItemSelect(selectedItem)}
          type="button"
          variant="default"
        >
          Next
        </Button>
      </div>
    </>
  );
};
